import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CHROME_STORE_URL } from '../constants';
import { CloseIcon } from './Icons';

const STORAGE_KEY = 'tubly_disclaimer_dismissed_v1';
// This URL would be replaced with the actual store link in a real deployment.

const DisclaimerBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  const setBannerOffset = useCallback((height: number) => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.style.setProperty('--disclaimer-banner-height', `${height}px`);
  }, []);

  const updateBannerHeight = useCallback(() => {
    if (!bannerRef.current) {
      setBannerOffset(0);

      return;
    }

    setBannerOffset(bannerRef.current.offsetHeight);
  }, [setBannerOffset]);

  useEffect(() => {
    // We only want to run this check on the client-side, after hydration.
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed !== 'true') {
        setIsVisible(true);
      }
    } catch (error) {
      // localStorage might be disabled (e.g., in private browsing mode).
      // We'll show the banner in this case.
      console.warn('Could not access localStorage for disclaimer banner.');
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setBannerOffset(0);

      return undefined;
    }

    updateBannerHeight();

    const element = bannerRef.current;
    if (!element) {
      return () => {
        setBannerOffset(0);
      };
    }

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateBannerHeight);

      return () => {
        window.removeEventListener('resize', updateBannerHeight);
        setBannerOffset(0);
      };
    }

    const resizeObserver = new ResizeObserver(updateBannerHeight);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      setBannerOffset(0);
    };
  }, [isVisible, setBannerOffset, updateBannerHeight]);

  const handleDismiss = () => {
    setIsVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (error) {
      console.warn('Could not save disclaimer dismissal state to localStorage.');
    }

    setBannerOffset(0);
  };

  if (!isVisible) {

    return null;
  }

  return (
    <div
      ref={bannerRef}
      className="bg-yellow-400 text-yellow-900 text-sm py-2.5 px-4 z-[60] fixed top-0 left-0 right-0"
    >
      <div className="container mx-auto text-center relative px-10">
        <p>
          <span className="font-semibold">Official Tubly:</span> tubly.app &amp; the official{' '}
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" className="underline hover:text-yellow-900/70 font-bold">
            Chrome Web Store
          </a>
          . We are not affiliated with <em>tubly.download</em>.
        </p>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss disclaimer"
          className="absolute right-0 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-yellow-500/50 transition-colors"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default DisclaimerBanner;
